import { ReactNode, useState } from 'react'

import AdjustmentsIcon from '@heroicons/react/outline/AdjustmentsIcon'
import CogIcon from '@heroicons/react/outline/CogIcon'
import LogoutIcon from '@heroicons/react/outline/LogoutIcon'
import ShieldCheckIcon from '@heroicons/react/outline/ShieldCheckIcon'
import SupportIcon from '@heroicons/react/outline/SupportIcon'
import UserCircleIcon from '@heroicons/react/outline/UserCircleIcon'
import { Link } from 'react-router-dom'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { useI18n } from '@tribeplatform/react-components/i18n'
import {
  SystemSpaceSlug,
  useAuthToken,
  useSpaceUtil,
} from '@tribeplatform/react-sdk/hooks'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'

import { useLogout } from '../../../hooks/useLogout'
import { SupportModal } from '../../SupportModal'
import { NavbarControlItem } from '../constants'

type ControlProfileItem = {
  name?: string
  href?: string
  key?: string
  icon?: ReactNode
  as?: 'a' | Link
  action?: () => void
}

export type NavbarControlProfileProps = {
  item: NavbarControlItem
  presentation: ReactNode
}

export const NavbarControlProfile = ({
  item,
  presentation,
}: NavbarControlProfileProps) => {
  const intl = useI18n()
  const {
    data: { network },
  } = useAuthToken()

  const { logout } = useLogout()
  const [supportModal, setSupportModal] = useState<boolean>(false)
  const { generatePath, generateProfilePath } = useSpaceUtil()

  const [canUpdateNetwork] = hasScopesPermission(network, ['updateNetwork'])
  const [canGetModerations] = hasScopesPermission(network, ['getModerations'])

  const accountSettingsPath = generatePath(SystemSpaceSlug.PROFILE_SETTINGS)

  let profileItems: Array<ControlProfileItem> = [
    {
      name: intl.$t({
        defaultMessage: 'Your Profile',
        id: 'Navbar.Controls.YourProfile',
      }),
      href: generateProfilePath(),
      icon: <UserCircleIcon />,
      key: 'your-profile',
    },
    {
      name: intl.$t({
        defaultMessage: 'Account Settings',
        id: 'Navbar.Controls.AccountSettings',
      }),
      href: `${accountSettingsPath}/account`,
      icon: <AdjustmentsIcon />,
      key: 'account-settings',
    },
  ]
  if (canUpdateNetwork) {
    profileItems.push({ name: '-' })
    profileItems.push({
      name: intl.$t({
        defaultMessage: 'Administration',
        id: 'Generics.Administration',
      }),
      href: '/manage/people/members',
      icon: <CogIcon />,
      key: 'administration',
    })
  }
  if (canGetModerations) {
    profileItems.push({
      name: intl.$t({
        defaultMessage: 'Moderation',
        id: 'Generics.Moderation',
      }),
      href: '/settings/moderation',
      icon: <ShieldCheckIcon />,
      key: 'customizer',
    })
  }
  if (canUpdateNetwork) {
    profileItems = profileItems.concat([
      {
        name: intl.$t({
          defaultMessage: 'Help & Community',
          id: 'RESERVED.Support',
        }),
        action: () => setSupportModal(true),
        icon: <SupportIcon />,
        key: 'help',
      },
    ])
  }
  profileItems = profileItems.concat([
    { name: '-' },
    {
      name: intl.$t({
        defaultMessage: 'Sign out',
        id: 'Generics.SignOut',
      }),
      action: () => logout(),
      icon: <LogoutIcon />,
      key: 'sign-out',
    },
  ])

  return (
    <>
      <Dropdown placement="bottom-end">
        <Dropdown.ButtonMinimal
          className="navbar-profile-menu"
          aria-label={item.label}
        >
          {presentation}
        </Dropdown.ButtonMinimal>
        <Dropdown.Items className="navbar-profile-menu__dropdown">
          {profileItems.map((item, index) => {
            if (!item) {
              return null
            }
            if (item?.name === '-') {
              return (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={`static-key-navbar-divider-${index}`}
                  className="w-full border-t border-line-subdued my-2 menu-divider"
                />
              )
            }

            if (item.as === 'a') {
              return (
                <Dropdown.Item
                  leadingIcon={item.icon as any}
                  key={item.name}
                  as="a"
                  href={item.href || '#'}
                  onClick={item.action}
                  data-id={item.key}
                >
                  {item.name}
                </Dropdown.Item>
              )
            }

            if (item.href) {
              return (
                <Dropdown.Item
                  leadingIcon={item.icon as any}
                  key={item.name}
                  as={Link}
                  to={item.href}
                  onClick={item.action}
                  data-id={item.key}
                >
                  {item.name}
                </Dropdown.Item>
              )
            }

            return (
              <Dropdown.Item
                leadingIcon={item.icon as any}
                key={item.name}
                onClick={item.action}
                data-id={item.key}
              >
                {item.name}
              </Dropdown.Item>
            )
          })}
        </Dropdown.Items>
      </Dropdown>
      <SupportModal
        open={supportModal}
        onClose={() => setSupportModal(false)}
      />
    </>
  )
}
